import {
  AUTH_ERROR_PATH,
  CALLBACK_PATH,
  LOGIN_PATH,
  LOGOUT_PATH,
  PRIVACY_POLICY_PATH,
  TERMS_OF_SERVICE_PATH,
} from './routePaths'

// The routes a signed-out visitor is allowed to sit on. Two consumers need the
// same set, which is what makes it worth naming once:
//
//  - Router.tsx's idle-session callback, which must not bounce a visitor off
//    these to `/login?expired=1` (it would fight the very login flow it
//    redirected into, and would evict someone reading the legal pages).
//  - resolveReturnPath, whose re-entry guard refuses to send a user back to one
//    of these after login — that would be a redirect loop.
export const UNAUTHENTICATED_PATHS: ReadonlyArray<string> = [
  LOGIN_PATH,
  CALLBACK_PATH,
  AUTH_ERROR_PATH,
  LOGOUT_PATH,
  PRIVACY_POLICY_PATH,
  TERMS_OF_SERVICE_PATH,
]

/**
 * True when `pathname` is one of the routes an unauthenticated visitor may
 * occupy.
 *
 * Matching is exact-or-segment-boundary, deliberately. Router.tsx previously
 * tested `pathname.indexOf('/login') >= 0`, which also matched any path merely
 * *containing* an auth path — `/my-login-notes` and `/albums/callback-history`
 * both counted as excluded, so an expiring session was silently never bounced
 * off them. Narrowing to a segment boundary fixes that latent bug.
 *
 * Query strings and hashes are stripped first: `/login?expired=1` is written by
 * Router.tsx itself and `/callback?code=…` is what Auth0 returns, so both have
 * to count.
 *
 * Matching is also case-INSENSITIVE, because react-router-dom@5.3.3 is:
 * `matchPath`'s `sensitive` option defaults to false, so `/Logout` genuinely
 * renders LogoutView. Comparing case-sensitively would classify `/Login` as an
 * ordinary authenticated route — which resolveReturnPath would then accept as a
 * post-login destination, sending the user straight back into `loginWithIdp()`.
 * That is precisely the redirect loop this predicate exists to prevent.
 */
export const isUnauthenticatedRoute = (pathname: string): boolean => {
  if (typeof pathname !== 'string' || pathname.length === 0) {
    return false
  }

  const [withoutHash] = pathname.split('#')
  const [pathOnly] = withoutHash.split('?')
  const withoutTrailingSlash =
    pathOnly.length > 1 && pathOnly.endsWith('/')
      ? pathOnly.slice(0, -1)
      : pathOnly
  const normalized = withoutTrailingSlash.toLowerCase()

  return UNAUTHENTICATED_PATHS.some(
    (path) => normalized === path || normalized.startsWith(`${path}/`),
  )
}
